import React from 'react';
import RefLink from '../shared/RefLink';
import NowName from '../shared/NowName';
import { ViewOrDownload, DownloadLink, DownloadAllLink } from '../shared/FileList';
import CompanyList from './CompanyList';
import SplitList from './SplitList';
import { fmtBytes } from './constants';

/**
 * The companies of a Screening request / record — one row each: the name
 * (with "(now: …)" after a rename), its UEN underneath, and a link to the
 * engagement it is tied to on the right. Shared by both detail pages.
 */
export const ScreeningCompaniesList = ({ entities = [] }) => (
  <CompanyList
    entities={entities}
    controlOf={(e) => (e.engagementId ? (
      <RefLink to={`/qm/engagements/${e.engagementId}`} className="text-xs text-blue-600 hover:text-blue-800 underline">
        View Engagement
      </RefLink>
    ) : null)}
  />
);

/**
 * The BizFile each company brought in on its Engagement Registration — QM
 * reads it to perform the screening, so it is listed here instead of one
 * View Engagement click per company.
 *
 * **A company with no BizFile still gets a row.** The missing one is what QM
 * needs to know about; listing only what exists would make a gap look like a
 * shorter group. Government agencies and overseas companies have no ACRA
 * BizFile at all (the Registration calls that slot "Registration document"
 * and does not require it), so an empty row is not necessarily a fault.
 *
 *   entities  [{ entityName, entityNameNow?, bizFiles: [{ id, originalName, sizeBytes }] }]
 *   hrefOf    (file) => download url
 */
export const ScreeningBizFileList = ({ entities = [], hrefOf }) => (
  <SplitList
    controlClass="shrink-0"
    rows={entities.flatMap((e, i) => {
      const key = e.id || `${e.entityId || 'entity'}-${i}`;
      const title = <NowName value={e.entityName} now={e.entityNameNow} />;
      if (!(e.bizFiles || []).length) {
        return [{ key, title, subtitle: <span className="text-slate-400">No BizFile on file</span>, control: null }];
      }
      return e.bizFiles.map((f) => ({
        key: `${key}-${f.id}`,
        title,
        subtitle: <>{f.originalName}{f.sizeBytes != null && ` · ${fmtBytes(f.sizeBytes)}`}</>,
        // Two separate things: reading it now (BizFiles are PDFs by rule, so
        // View opens a tab) and keeping a copy. The second one is a different
        // URL rather than the anchor's `download` attribute — that attribute
        // is ignored cross-origin, and the API is its own origin.
        control: (
          <span className="inline-flex items-center gap-3">
            <ViewOrDownload href={hrefOf(f)} name={f.originalName} />
            <DownloadLink href={`${hrefOf(f)}?download=1`} />
          </span>
        ),
      }));
    })}
  />
);

/** The whole request's BizFiles in one zip — only rendered when there is one. */
export const ScreeningBizFileDownloadAll = ({ href, count }) => (
  <DownloadAllLink href={href} count={count} />
);

